import { supabase } from '../lib/supabase';

export const periodLockService = {
  // Create period lock
  async createPeriodLock(lockData) {
    try {
      const { data, error } = await supabase?.from('period_locks')?.insert([lockData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create period lock error:', error);
      return { data: null, error: error?.message || 'Failed to create period lock' };
    }
  },

  // Get period locks for organization
  async getPeriodLocks(organizationId, options = {}) {
    try {
      const { page = 1, limit = 20, status, lockType, year, month } = options;
      const offset = (page - 1) * limit;

      let query = supabase?.from('period_locks')?.select(`
          *,
          created_by:created_by(full_name, email),
          locked_by:locked_by(full_name, email),
          unlocked_by:unlocked_by(full_name, email)
        `)?.eq('organization_id', organizationId)?.order('period_start_date', { ascending: false });

      if (status) query = query?.eq('lock_status', status);
      if (lockType) query = query?.eq('lock_type', lockType);
      
      if (year) {
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;
        query = query?.gte('period_start_date', startDate)?.lte('period_end_date', endDate);
      }
      
      if (month && year) {
        const monthStr = month?.toString()?.padStart(2, '0');
        const startDate = `${year}-${monthStr}-01`;
        const endDate = new Date(year, month, 0)?.toISOString()?.split('T')?.[0]; // Last day of month
        query = query?.gte('period_start_date', startDate)?.lte('period_end_date', endDate);
      }

      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get period locks error:', error);
      return { data: [], error: error?.message || 'Failed to fetch period locks' };
    }
  },

  // Update period lock status
  async updatePeriodLockStatus(lockId, newStatus, userId, reason = null) {
    try {
      const updateData = {
        lock_status: newStatus,
        updated_at: new Date()?.toISOString()
      };

      if (newStatus === 'soft_lock' || newStatus === 'hard_lock' || newStatus === 'closed') {
        updateData.locked_by = userId;
        updateData.locked_at = new Date()?.toISOString();
      } else if (newStatus === 'open') {
        updateData.unlocked_by = userId;
        updateData.unlocked_at = new Date()?.toISOString();
        if (reason) updateData.unlock_reason = reason;
      }

      const { data, error } = await supabase?.from('period_locks')?.update(updateData)?.eq('id', lockId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update period lock error:', error);
      return { data: null, error: error?.message || 'Failed to update period lock' };
    }
  },

  // Check if period is locked for specific date
  async checkPeriodLock(organizationId, checkDate) {
    try {
      const { data, error } = await supabase?.rpc('check_period_lock', {
          check_date: checkDate,
          org_id: organizationId
        });

      if (error) throw error;
      
      return { isLocked: data === true, error: null };
    } catch (error) {
      console.error('Check period lock error:', error);
      return { isLocked: false, error: error?.message || 'Failed to check period lock' };
    }
  },

  // Get period lock details for specific date
  async getPeriodLockForDate(organizationId, checkDate) {
    try {
      const { data, error } = await supabase?.from('period_locks')?.select(`
          *,
          locked_by:locked_by(full_name, email),
          unlocked_by:unlocked_by(full_name, email)
        `)?.eq('organization_id', organizationId)?.lte('period_start_date', checkDate)?.gte('period_end_date', checkDate)?.single();

      if (error && error?.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return { data: error?.code === 'PGRST116' ? null : data, error: null };
    } catch (error) {
      console.error('Get period lock for date error:', error);
      return { data: null, error: error?.message || 'Failed to fetch period lock' };
    }
  },

  // Lock period (soft lock -> hard lock -> closed)
  async lockPeriod(lockId, userId, lockType = 'soft_lock') {
    try {
      const validLockTypes = ['soft_lock', 'hard_lock', 'closed'];
      if (!validLockTypes?.includes(lockType)) {
        throw new Error(`Invalid lock type: ${lockType}`);
      }

      const result = await this.updatePeriodLockStatus(lockId, lockType, userId);
      return result;
    } catch (error) {
      console.error('Lock period error:', error);
      return { data: null, error: error?.message || 'Failed to lock period' };
    }
  },

  // Unlock period
  async unlockPeriod(lockId, userId, reason) {
    try {
      if (!reason || reason?.trim() === '') {
        throw new Error('Unlock reason is required');
      }

      const result = await this.updatePeriodLockStatus(lockId, 'open', userId, reason);
      return result;
    } catch (error) {
      console.error('Unlock period error:', error);
      return { data: null, error: error?.message || 'Failed to unlock period' };
    }
  },

  // Get period lock by ID
  async getPeriodLockById(lockId) {
    try {
      const { data, error } = await supabase?.from('period_locks')?.select(`
          *,
          created_by:created_by(full_name, email),
          locked_by:locked_by(full_name, email),
          unlocked_by:unlocked_by(full_name, email)
        `)?.eq('id', lockId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get period lock error:', error);
      return { data: null, error: error?.message || 'Failed to fetch period lock' };
    }
  },

  // Get period locks summary
  async getPeriodLocksSummary(organizationId, year = null) {
    try {
      let query = supabase?.from('period_locks')?.select('lock_status, lock_type, period_start_date, period_end_date')?.eq('organization_id', organizationId);

      if (year) {
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;
        query = query?.gte('period_start_date', startDate)?.lte('period_end_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Calculate summary statistics
      const summary = {
        totalPeriods: data?.length || 0,
        byStatus: {
          open: 0,
          soft_lock: 0,
          hard_lock: 0,
          closed: 0
        },
        byType: {},
        currentlyLocked: 0
      };

      data?.forEach(lock => {
        const status = lock?.lock_status;
        const type = lock?.lock_type;
        
        if (summary?.byStatus?.[status] !== undefined) {
          summary.byStatus[status]++;
        }

        if (!summary?.byType?.[type]) {
          summary.byType[type] = 0;
        }
        summary.byType[type]++;

        if (status !== 'open') {
          summary.currentlyLocked++;
        }
      });

      return { data: summary, error: null };
    } catch (error) {
      console.error('Get period locks summary error:', error);
      return { data: null, error: error?.message || 'Failed to fetch period locks summary' };
    }
  },

  // Create monthly period locks for a year
  async createMonthlyPeriodLocks(organizationId, userId, year, lockType = 'month_end') {
    try {
      const monthlyLocks = [];
      
      for (let month = 1; month <= 12; month++) {
        const monthStr = month?.toString()?.padStart(2, '0');
        const startDate = `${year}-${monthStr}-01`;
        const endDate = new Date(year, month, 0)?.toISOString()?.split('T')?.[0]; // Last day of month
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];

        monthlyLocks?.push({
          organization_id: organizationId,
          created_by: userId,
          period_name: `${monthNames?.[month - 1]} ${year}`,
          period_start_date: startDate,
          period_end_date: endDate,
          lock_status: 'open',
          lock_type: lockType
        });
      }

      const { data, error } = await supabase?.from('period_locks')?.insert(monthlyLocks)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create monthly period locks error:', error);
      return { data: null, error: error?.message || 'Failed to create monthly period locks' };
    }
  },

  // Delete period lock
  async deletePeriodLock(lockId) {
    try {
      const { error } = await supabase?.from('period_locks')?.delete()?.eq('id', lockId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete period lock error:', error);
      return { error: error?.message || 'Failed to delete period lock' };
    }
  }
};